import React, {useCallback, useEffect, useMemo, useState} from "react"
import {Button, Card, Col, Descriptions, Modal, Row, Select, Table, Tag} from "antd"
import {Helmet} from "react-helmet-async"
import {useDispatch, useSelector} from "react-redux"
import {NavLink, useHistory, useLocation, useParams} from "react-router-dom"
import {StateType} from "../../store/store"
import {BackButton} from "../../components/BackButton/BackButton"
import {Loading} from "../../components/Loading/Loading"
import moment from "moment/moment"
import {deleteObjectGroup, getObjectGroup, setObjectGroup, setObjectGroupSideFilter, setWSObjectGroup} from "../../store/objectGroupReducer"
import {center} from "../../helps/tableHelper"
import {getObjects, setObjects, setWSObject} from "../../store/objectReducer"
import {filterToString} from "../../helps/filterToString"
import {isActiveOptionsSelect, limitOptionsSelect} from "../../helps/optionsSelect"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {AttributeRuleType} from "../../types/types"
import {FormSelect} from "../../components/Form/FormSelect"
import {ObjectGroupSchedules} from "./ObjectGroupSchedule/ObjectGroupSchedules"
import {FormInput} from "../../components/Form/FormInput"

export const ObjectGroup = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const id = parseInt(useParams<{ id: string }>()?.id, 10)
    const status = useSelector((state: StateType) => state.appReducer.status)
    const objectGroup = useSelector((state: StateType) => state.objectGroupReducer.objectGroup)
    const filter = useSelector((state: StateType) => state.objectGroupReducer.sideFilter)
    const objects = useSelector((state: StateType) => state.objectReducer.objects)
    const ws = useSelector((state: StateType) => state.objectGroupReducer.ws)
    const wsObject = useSelector((state: StateType) => state.objectReducer.ws)
    const [showModal, setShowModal] = useState(false)
    const [showModalSchedules, setShowModalSchedules] = useState(false)
    const controller = useMemo(() => new AbortController(), [id])

    const columns = useMemo(() => {
        const array: any = [
            {
                title: 'Название',
                dataIndex: 'name',
                key: 'name',
                align: center,
            },
            {
                title: 'Статус',
                dataIndex: 'active',
                key: 'active',
                render: (active: boolean) => {
                    return (
                        <Tag color={active ? 'green' : 'volcano'} style={{marginRight: 0}}>{active ? 'Активно' : 'Не активно'}</Tag>
                    )
                },
                align: center,
            },
        ]

        if (objectGroup) {
            const parsed: Array<AttributeRuleType> = JSON.parse(objectGroup.attributes)

            if (Array.isArray(parsed)) {
                parsed.filter((i) => i.showInTable && !i.is_deleted)
                    .sort((a, b) => a.pos - b.pos)
                    .forEach((i) => {
                        array.push({
                            title: i.name,
                            dataIndex: `${i.alias}_value`,
                            key: `${i.alias}_value`,
                            width: 'fit-content',
                            align: center,
                            render: (value: Array<string> | string) => {
                                let currentValue = null

                                if (Array.isArray(value)) {
                                    currentValue = value.map((option: string) => {
                                        const opt = i.options?.find((ruleOption) => ruleOption.value === option)
                                        if (opt) return <div key={opt.value}>{opt.label}</div>
                                    })
                                } else if (value?.includes('option_')) {
                                    const opt = i.options?.find((ruleOption) => ruleOption.value === value)
                                    if (opt) currentValue = <div key={opt.value}>{opt.label}</div>
                                } else currentValue = <div>{value}</div>

                                return currentValue
                            },
                        })
                    })
            }
        }

        return array
    }, [objectGroup])

    useEffect(() => {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter)) {
            dispatch(setObjectGroupSideFilter({...filterString}))
        } else if (location.search !== filterToString(filter)) {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    useEffect(() => {
        if (id) dispatch(getObjectGroup({id, controller}))

        return () => {
            controller.abort()
            dispatch(setObjectGroup(null))
        }
    }, [dispatch, id, controller])

    const getObjectsFunc = useCallback((withoutLoadingIndicator = false) => {
        if (id && location?.search) dispatch(getObjects({idObjectGroup: id, ...filterFromString(location.search), controller, withoutLoadingIndicator}))
    }, [dispatch, id, location, controller])

    useEffect(() => {
        getObjectsFunc()

        return () => {
            dispatch(setObjects(null))
        }
    }, [dispatch, getObjectsFunc])

    const deleteHandler = useCallback(async () => {
        if (objectGroup?.id) {
            const {payload}: any = await dispatch(deleteObjectGroup(objectGroup.id))
            if (payload?.status === 0) {
                setShowModal(false)
                if (payload?.message === 'Удалено') history.goBack()
                else dispatch(getObjectGroup({id, controller}))
            }
        }
    }, [objectGroup, history, id, controller])

    useEffect(() => {
        if (ws && ws.id === id) {
            dispatch(getObjectGroup({id, controller, withoutLoadingIndicator: true}))
        }
        dispatch(setWSObjectGroup(null))
    }, [dispatch, ws, id, controller])

    useEffect(() => {
        if (wsObject && wsObject.idParent === id) {
            getObjectsFunc(true)
        }
        dispatch(setWSObject(null))
    }, [dispatch, id, wsObject, getObjectsFunc])

    const data = useMemo(() => objects?.data?.map(i => {
        const row: any = {
            key: i.id,
            id: i.id,
            name: i.name,
            attributes: i.attributes,
            active: i.active,
            deleted: i.deleted,
        }

        const parsed = JSON.parse(i.attributes)

        if (Array.isArray(parsed)) {
            parsed.forEach((p: any) => {
                row[`${p.alias}_value`] = p.value
            })
        }

        return row
    }), [objects])

    if (!objectGroup) return <Loading/>

    return (
        <div className="description-page">
            <Helmet>
                <title>{objectGroup?.name}</title>
            </Helmet>
            <div className="description-header">
                <BackButton onClick={() => history.goBack()}/>
                <div>
                    <Button type="link" onClick={() => setShowModalSchedules(true)}>
                        {objectGroup.hasAnySchedule ? 'Изменить расписание' : 'Установить расписание'}
                    </Button>
                    {!objectGroup?.deleted ? <Button type="link" onClick={() => history.push(`/objectGroupForm`, {objectGroup})}>Редактировать</Button> : null}
                    <Button type="link" danger onClick={() => setShowModal(true)}>Удалить</Button>
                </div>
            </div>

            <Descriptions bordered column={{xs: 1, sm: 1, md: 2}}>
                <Descriptions.Item label="Название">{objectGroup?.name}</Descriptions.Item>
                <Descriptions.Item label="ID">{objectGroup?.id}</Descriptions.Item>
                <Descriptions.Item label="Компания">
                    <NavLink to={`/company/${objectGroup.idCompany}`}>{objectGroup.nameCompany}</NavLink>
                </Descriptions.Item>
                <Descriptions.Item label="Статус">
                    <div className="description-item-status">
                        <Tag color={objectGroup.active ? 'green' : 'volcano'} style={{marginRight: 0}}>{objectGroup.active ? 'Активно' : 'Не активно'}</Tag>
                        {objectGroup?.deleted ? <Tag color="volcano" style={{marginRight: 0}}>Удалено</Tag> : null}
                    </div>
                </Descriptions.Item>
                <Descriptions.Item label="Создана">{moment(objectGroup?.createdAt).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
                <Descriptions.Item label="Обновлена">{moment(objectGroup?.updatedAt).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
                <Descriptions.Item label="Расписание активности">
                    <div className="description-item-status">
                        <Tag color={objectGroup.hasAnySchedule ? 'green' : 'volcano'} style={{marginRight: 0}}>
                            {objectGroup.hasAnySchedule ? 'Установлено' : 'Не установлено'}
                        </Tag>
                    </div>
                </Descriptions.Item>
                <Descriptions.Item label="Часовой пояс">{objectGroup.timeOffsetName}</Descriptions.Item>
                <Descriptions.Item label="Описание">{objectGroup.description}</Descriptions.Item>
            </Descriptions>

            <div className="tabled-side">
                <Row gutter={[24, 0]}>
                    <Col xs={24}>
                        <Card variant="borderless"
                              className="criclebox tablespace mb-24"
                              title={
                                  <div className="table-title">
                                      <div>Объекты</div>
                                      <Button onClick={() => history.push('/objectForm', {idCompany: objectGroup.idCompany, idObjectGroup: id})} type="link" size="middle" style={{marginTop: 5}}>
                                          Добавить
                                      </Button>
                                  </div>
                              }
                              extra={
                                  <div className="table-title-extra">
                                      <FormInput placeholder="Поиск"
                                                 containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                 value={filter?.search}
                                                 name="search"
                                                 onChange={(e) => history.replace({search: filterToString({...filter, search: e.target.value, offset: 0})})}
                                                 allowClear
                                      />

                                      <FormSelect value={filter?.status}
                                                  containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                                  dropdownStyle={{minWidth: 'min-content'}}
                                                  onChange={(value) => history.replace({search: filterToString({...filter, status: value, offset: 0})})}
                                                  options={isActiveOptionsSelect}
                                                  placeholder="Статус"
                                                  allowClear
                                                  placement="bottomRight"
                                      />

                                      <FormSelect value={filter?.limit}
                                                  containerStyle={{marginBottom: 0, width: 'auto'}}
                                                  dropdownStyle={{minWidth: 'min-content'}}
                                                  onChange={(value) => history.replace({search: filterToString({...filter, limit: value, offset: 0})})}
                                                  options={limitOptionsSelect}
                                                  placement="bottomRight"
                                      />
                                  </div>
                              }
                        >
                            <div className="table-responsive">
                                {
                                    objects
                                        ? <Table columns={columns}
                                                 dataSource={data}
                                                 pagination={filter.limit ? {
                                                     position: objects?.data?.length >= 10 ? ['bottomLeft', 'topLeft'] : ['bottomLeft'],
                                                     pageSize: filter.limit,
                                                     hideOnSinglePage: true,
                                                     total: objects.total,
                                                     current: filter?.offset / filter?.limit + 1,
                                                     onChange: (page, pageSize) => {
                                                         const obj = {
                                                             limit: pageSize,
                                                             offset: (page - 1) * pageSize,
                                                         }
                                                         history.replace({search: filterToString({...filter, ...obj})})
                                                     },
                                                     showSizeChanger: false,
                                                 } : false}
                                                 className="ant-border-space"
                                                 onRow={({id}) => ({
                                                     onClick: () => history.push(`/object/${id}`),
                                                     onMouseDown: (e) => e.button === 1 && window.open(`/object/${id}`, '_blank', 'noopener,noreferrer')
                                                 })}
                                                 rowClassName={({deleted}) => deleted ? 'table-row-deleted' : ''}
                                        />
                                        : <Loading/>
                                }
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>

            {
                showModalSchedules
                    ? <ObjectGroupSchedules idGroup={objectGroup.id}
                                            dispatch={dispatch}
                                            showModalSchedules={showModalSchedules}
                                            setShowModalSchedules={setShowModalSchedules}
                    />
                    : null
            }

            <Modal title={`Вы уверены что хотите удалить группу`}
                   centered
                   open={showModal}
                   onOk={deleteHandler}
                   onCancel={() => setShowModal(false)}
                   okText="Да"
                   okButtonProps={{loading: status === 'loading'}}
            />

        </div>
    )
}